import { Tooltip } from 'slab'
import { cn, formatMoney } from 'utils'

import { type SpectrumData } from './types'
import { DARK_GREEN_COLOR, DARK_RED_COLOR, GREEN_COLOR, RED_COLOR } from './utils'

type CostImpactSpectrumProps = {
  data: SpectrumData[]
}

export default function CostImpactSpectrum ({ data }: CostImpactSpectrumProps) {
  if (data.length === 0) return null

  const payingMore = data.find(d => d.name === 'Paying More')
  const payingLess = data.find(d => d.name === 'Paying Less')

  return (
    <div className='flex flex-col gap-1'>
      <div className='w-full h-10 flex items-center gap-2'>
        {data.map((item) => (
          <Tooltip key={item.name} content={`${item.value} employees (${item.percentage}%) are ${item.name.split(' ').join(' ').toLowerCase()} than their current contribution.`}>
            <div
              className={cn('h-full text-white flex items-center justify-start flex-nowrap whitespace-nowrap rounded-lg text-xs font-medium hover:opacity-90 hover:scale-[102%] cursor-default transition-all duration-500 ease-in-out', item.percentage > 0 && 'pl-2')}
              style={{
                width: `${item.percentage}%`,
                background: item.name === 'Paying More'
                  ? `linear-gradient(to bottom, ${RED_COLOR}, ${DARK_RED_COLOR})`
                  : `linear-gradient(to bottom, ${GREEN_COLOR}, ${DARK_GREEN_COLOR})`,
                boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.25), 0 1px 2px rgba(0,0,0,0.1)',
                border: `1px solid ${item.name === 'Paying More' ? DARK_RED_COLOR : DARK_GREEN_COLOR}`,
              }}
            >
              {item.percentage > 0 && (
                <>
                  {item.percentage}%
                  {item.percentage >= 15 && ` ${item.name}`}
                </>
              )}
            </div>
          </Tooltip>
        ))}
      </div>
      <div className='flex justify-between text-xs text-gray-500'>
        <div>
          {payingMore?.extremeEmployees && (
            <Tooltip content={`The most impacted employee${payingMore.extremeEmployees.employees.length > 1 ? 's are' : ' is'} paying ${formatMoney(Math.abs(payingMore.extremeEmployees.amount))} more per month.`}>
              <span className='cursor-default'>
                {payingMore.extremeEmployees.employees[0]}
                {payingMore.extremeEmployees.employees.length > 1 && ` and ${payingMore.extremeEmployees.employees.length - 1} others`}{' '}
                <span className='font-medium' style={{ color: DARK_RED_COLOR }}>
                  {formatMoney(Math.abs(payingMore.extremeEmployees.amount))}/mo
                </span>
              </span>
            </Tooltip>
          )}
        </div>
        <div>
          {payingLess?.extremeEmployees && (
            <Tooltip content={`Paying ${formatMoney(Math.abs(payingLess.extremeEmployees.amount))} less per month.`}>
              <span className='cursor-default'>
                {payingLess.extremeEmployees.employees[0]}
                {payingLess.extremeEmployees.employees.length > 1 && ` and ${payingLess.extremeEmployees.employees.length - 1} others`}{' '}
                <span className='font-medium' style={{ color: DARK_GREEN_COLOR }}>
                  {formatMoney(Math.abs(payingLess.extremeEmployees.amount))}/mo
                </span>
              </span>
            </Tooltip>
          )}
        </div>
      </div>
    </div>
  )
}
